import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "#portfolio", label: "Portfolio", testid: "nav-portfolio-link" },
  { id: "#ueber-mich", label: "Über mich", testid: "nav-about-link" },
  { id: "/momente", label: "Momente", testid: "nav-momente-link" },
  { id: "#pakete", label: "Pakete", testid: "nav-packages-link" },
  { id: "#termine", label: "Termine", testid: "nav-booking-link" },
  { id: "#mein-termin", label: "Mein Termin", testid: "nav-status-link" },
  { id: "#kontakt", label: "Kontakt", testid: "nav-contact-link" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (id.startsWith("/")) {
      navigate(id);
    } else if (location.pathname === "/") scrollToId(id);
    else navigate("/" + id);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 border-b border-white/10 bg-[#0A0A0C]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 lg:px-12">
        <Link to="/" data-testid="nav-logo-link" className="group flex items-center gap-3">
          <img src="/assets/logo-mark.png" alt="Leno Fotografie Logo" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/15" />
          <span className="flex items-baseline gap-2">
            <span className="font-sans text-xl font-semibold tracking-[0.18em] text-white transition-colors group-hover:text-gold">
              LENO
            </span>
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-gold xl:inline">
              Fotografie
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex xl:gap-8">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={l.testid}
              onClick={(e) => go(e, l.id)}
              className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-white xl:text-[11px] xl:tracking-[0.25em]"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <span className="hidden items-center gap-2 whitespace-nowrap 2xl:flex" data-testid="nav-status-badge">
            <span className="h-1.5 w-1.5 rounded-full bg-gold" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              2026 Buchungen offen
            </span>
          </span>
          <Link
            to="/admin"
            data-testid="nav-admin-login-button"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-zinc-400 transition-colors hover:border-gold hover:text-gold"
            aria-label="Admin Login"
          >
            <Lock size={14} />
          </Link>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
            aria-label="Menü"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#0A0A0C]/95 lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.id}
                  data-testid={`mobile-${l.testid}`}
                  onClick={(e) => go(e, l.id)}
                  className="py-3 font-serif text-2xl text-zinc-300 transition-colors hover:text-gold"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
